import {
  Button,
  Icons,
  List,
  Space,
  Table,
  useModalForm,
  useTable,
} from '@pankod/refine-antd'
import {
  GetListResponse,
  IResourceComponentsProps,
  useDelete,
  useInvalidate,
} from '@pankod/refine-core'
import React from 'react'
import Head from 'next/head'

import {
  LandingPageCategory,
  LandingPage,
  ShowcaseLandingPageRequest,
} from '@resources/lms-management/landing-page/types'
import ShowcaseModalForm from '@resources/lms-management/landing-page/blocks/ShowcaseModalForm'
import { sanitizeLandingPageTargeting } from '@resources/lms-management/landing-page/utils'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import { TCommonError } from 'src/interfaces/common'
import { PopDeleteConfirm } from '@components/PopDeleteConfirm'

const LMSLandingPageList: React.FC<
  IResourceComponentsProps<GetListResponse<LandingPage>>
> = ({ options }) => {
  const invalidates = useInvalidate()

  const handleInvalidateList = () => {
    invalidates({
      invalidates: ['list'],
      dataProviderName: 'lms',
      resource: 'diklat/landing-page',
    })
  }

  const { tableProps } = useTable({
    dataProviderName: 'lms',
    resource: 'diklat/landing-page',
    hasPagination: false,
  })
  const { mutateAsync: doDelete } = useDelete()
  const {
    modalProps: createModalProps,
    formProps: createFormProps,
    show: createShow,
    close: createClose,
  } = useModalForm<{}, TCommonError, ShowcaseLandingPageRequest>({
    action: 'create',
    resource: 'diklat/landing-page',
    dataProviderName: 'lms',
    autoResetForm: true,
    redirect: false,
    successNotification: {
      message: 'Showcase berhasil ditambah',
      type: 'success',
      description: 'Sukses',
    },
    onMutationSuccess: () => {
      handleInvalidateList()
    },
    errorNotification: (error) =>
      showErrorNotification(error, 'Terdapat gangguan saat menambah showcase'),
  })
  const {
    modalProps: editModalProps,
    formProps: editFormProps,
    show: editShow,
    close: editClose,
    submit: editSubmit,
  } = useModalForm<{}, TCommonError, Partial<ShowcaseLandingPageRequest>>({
    action: 'edit',
    resource: 'diklat/landing-page',
    dataProviderName: 'lms',
    autoResetForm: true,
    redirect: false,
    successNotification: {
      message: 'Showcase berhasil diubah',
      type: 'success',
      description: 'Sukses',
    },
    onMutationSuccess: () => {
      handleInvalidateList()
    },
    submit: ({
      badge,
      description,
      status,
      title,
      url,
      sortOrder,
      detail,
      category,
      provinceCodes,
      whitelistUsers,
      whitelistEmailDomains,
    }) => {
      return sanitizeLandingPageTargeting({
        badge,
        description,
        status,
        title,
        url,
        sortOrder,
        detail,
        category: category as LandingPageCategory | undefined,
        provinceCodes: provinceCodes as string[] | undefined,
        whitelistUsers: whitelistUsers as string[] | undefined,
        whitelistEmailDomains: whitelistEmailDomains as string[] | undefined,
      })
    },
    errorNotification: (error) =>
      showErrorNotification(error, 'Terdapat gangguan saat mengubah showcase'),
  })
  return (
    <List
      title="Daftar Showcase Landing Page Diklat"
      headerButtons={() => (
        <Button
          icon={<Icons.PlusOutlined />}
          type="primary"
          onClick={() => createShow()}
        >
          Tambah Showcase
        </Button>
      )}
    >
      <Head>
        <title>LMS Admin | {options?.label}</title>
      </Head>
      <Table
        {...tableProps}
        size="small"
        columns={[
          {
            title: 'ID',
            dataIndex: 'id',
            width: 100,
          },
          {
            title: 'Judul',
            dataIndex: 'title',
          },
          {
            title: 'Aksi',
            width: 120,
            render: (record) => (
              <Space>
                <Button
                  size="small"
                  onClick={() => editShow(record.id)}
                  icon={<Icons.EditOutlined />}
                >
                  Edit
                </Button>
                <PopDeleteConfirm
                  title="Apakah Anda yakin ingin menghapus item ini?"
                  okText="Hapus"
                  cancelText="Batal"
                  okButtonProps={{ danger: true }}
                  onConfirm={async () =>
                    await doDelete(
                      {
                        dataProviderName: 'lms',
                        id: record.id,
                        resource: 'diklat/landing-page',
                        errorNotification: (error) =>
                          showErrorNotification(
                            error,
                            'Terdapat gangguan saat menghapus showcase',
                          ),
                        successNotification: {
                          message: 'Showcase berhasil dihapus',
                          type: 'success',
                          description: 'Sukses',
                        },
                      },
                      {
                        onSettled: () => {
                          handleInvalidateList()
                        },
                      },
                    )
                  }
                  placement="topRight"
                >
                  <Button
                    danger
                    icon={<Icons.DeleteOutlined />}
                    block
                    size="small"
                  >
                    Hapus
                  </Button>
                </PopDeleteConfirm>
              </Space>
            ),
          },
        ]}
      />
      <ShowcaseModalForm
        formProps={createFormProps}
        modalProps={createModalProps}
        mode="create"
        onClose={createClose}
      />
      <ShowcaseModalForm
        formProps={editFormProps}
        modalProps={editModalProps}
        mode="edit"
        onClose={editClose}
        onSubmit={editSubmit}
      />
    </List>
  )
}

export { LMSLandingPageList }
